import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Flex, Heading, HStack, IconButton, Table, Tbody, Td, Text,
  Th, Thead, Tr, TableContainer, FormControl, FormLabel, Badge, Input,
  NumberInput, NumberInputField, Spinner, Tooltip, Checkbox,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdMap, MdCheck, MdClose, MdEdit } from 'react-icons/md';
import {
  fetchProviderCoverageAreas, deleteCoverageAreaById,
  addCoverageAreaToProvider, updateCoverageAreaById, getGeoCode,
} from '../Action/action';
import MapPreviewModal from './MapPreviewModal';
import CoverageMapModal from './CoverageMapModal';

interface Props { providers: any[]; preSelectedProvider?: any; }

const EMPTY_FORM = { name: '', rangeInKm: 10, rangeInKmStr: '10', lat: 0, lng: 0, city: '', state: '', isHospital: false, ambulancePlaced: false };

function ManageCoverageAreas({ providers, preSelectedProvider }: Props) {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<any>(preSelectedProvider || null);
  const [coverageAreas, setCoverageAreas] = useState<any[]>([]);
  const [coverageSearch, setCoverageSearch] = useState('');
  const [coveragePage, setCoveragePage] = useState(1);
  const COVERAGE_PAGE_SIZE = 10;

  useEffect(() => {
    if (preSelectedProvider) {
      setSelectedProvider(preSelectedProvider);
      setSearch(preSelectedProvider.operatorName);
    } else {
      setSelectedProvider(null);
      setSearch('');
    }
  }, [preSelectedProvider]);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState<(typeof EMPTY_FORM & { rangeInKmStr?: string }) | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null); // null = add mode
  const [isLocating, setIsLocating] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [coverageMapOpen, setCoverageMapOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredProviders = providers.filter(p =>
    p.operatorName?.toLowerCase().includes(search.toLowerCase()) ||
    p.address?.city?.toLowerCase().includes(search.toLowerCase())
  );

  const loadCoverageAreas = async (providerId: string) => {
    setIsLoading(true);
    try {
      const res = await fetchProviderCoverageAreas(providerId);
      const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setCoverageAreas(raw);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProvider) loadCoverageAreas(selectedProvider._id);
    else { setCoverageAreas([]); setForm(null); setCoverageSearch(''); }
  }, [selectedProvider]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelectProvider = (p: any) => {
    setSelectedProvider(p);
    setSearch(p.operatorName);
    setShowDropdown(false);
    setForm(null);
    setEditingId(null);
  };

  const handleDelete = async (area: any) => {
    if (area._id) await deleteCoverageAreaById(area._id);
    setCoverageAreas(prev => prev.filter(a => a._id !== area._id));
  };

  const handleEdit = (area: any) => {
    setEditingId(area._id);
    setForm({
      name: area.name || '',
      rangeInKm: area.rangeInKm || 10,
      rangeInKmStr: String(area.rangeInKm || 10),
      lat: area.location?.coordinates?.[1] || 0,
      lng: area.location?.coordinates?.[0] || 0,
      city: area.city || '',
      state: area.state || '',
      isHospital: area.isHospital || false,
      ambulancePlaced: area.ambulancePlaced || false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLocate = async () => {
    if (!form?.name) return;
    setIsLocating(true);
    try {
      const geo = await getGeoCode(form.name);
      if (geo?.lat && geo?.lng) {
        setForm(p => p ? { ...p, lat: geo.lat, lng: geo.lng, city: geo.city || '', state: geo.state || '' } : p);
        setMapOpen(true);
      }
    } finally {
      setIsLocating(false);
    }
  };

  const handleSave = async () => {
    if (!form?.name || !form.lat || !form.lng || !selectedProvider) return;
    setIsSaving(true);
    try {
      const payload = {
        name: form.name, city: form.city, state: form.state, rangeInKm: form.rangeInKm,
        isHospital: form.isHospital, ambulancePlaced: form.ambulancePlaced,
        location: { type: 'Point', coordinates: [form.lng, form.lat] },
      };
      if (editingId) {
        await updateCoverageAreaById(editingId, payload);
      } else {
        await addCoverageAreaToProvider(selectedProvider._id, payload);
      }
      await loadCoverageAreas(selectedProvider._id);
      setForm(null);
      setEditingId(null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelForm = () => { setForm(null); setEditingId(null); };

  const isLocated = !!(form?.lat && form?.lng);
  const isEditMode = editingId !== null;

  return (
    <Box p={6} bg="white" borderRadius="lg" shadow="sm" borderWidth={1}>

      {/* Header */}
      <Flex align="center" justify="space-between" mb={5} borderBottomWidth={1} pb={4}>
        <Heading size="md" color="teal.600">Manage Coverage Areas</Heading>
        {selectedProvider && !form && (
          <Button leftIcon={<MdAdd />} colorScheme="teal" size="sm" onClick={() => { setForm({ ...EMPTY_FORM }); setEditingId(null); }}>
            Add Coverage Area
          </Button>
        )}
      </Flex>

      {/* Searchable Provider Selector */}
      <FormControl mb={5} maxW="440px">
        <FormLabel fontWeight="bold">Select Provider</FormLabel>
        <Box position="relative" ref={dropdownRef}>
          <Input
            bg="gray.50" placeholder="Search by name or city..."
            value={search}
            onChange={e => { setSearch(e.target.value); setShowDropdown(true); if (!e.target.value) { setSelectedProvider(null); } }}
            onFocus={() => setShowDropdown(true)}
          />
          {showDropdown && filteredProviders.length > 0 && (
            <Box position="absolute" top="100%" left={0} right={0} zIndex={999} bg="white"
              borderWidth={1} borderRadius="md" shadow="lg" maxH="220px" overflowY="auto">
              {filteredProviders.map(p => (
                <Box key={p._id} px={3} py={2} cursor="pointer" _hover={{ bg: 'teal.50' }}
                  onMouseDown={() => handleSelectProvider(p)}>
                  <Text fontSize="sm" fontWeight="medium">{p.operatorName}</Text>
                  <Text fontSize="xs" color="gray.500">{p.address?.city}, {p.address?.state}</Text>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </FormControl>

      {/* Add / Edit Form Panel */}
      {form && (
        <Box
          mb={4}
          p={4}
          bg={isEditMode ? 'orange.50' : 'blue.50'}
          border="1px solid"
          borderColor={isEditMode ? 'orange.200' : 'blue.200'}
          borderRadius="md"
        >
          {/* Header */}
          <Flex justify="space-between" align="center" mb={3}>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color={isEditMode ? 'orange.700' : 'blue.700'}
            >
              {isEditMode ? 'Edit Coverage Area' : 'Add Coverage Area'}
            </Text>

            <IconButton
              aria-label="Close"
              icon={<MdClose />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={handleCancelForm}
            />
          </Flex>

          {/* Main Fields */}
          <Flex gap={3} mb={3} alignItems="flex-end" wrap="wrap">
            <Box flex="2" minW="160px">
              <Text fontSize="xs" mb={1} fontWeight="medium">Area Name <Text as="span" color="red.400">*</Text></Text>
              <Input
                size="sm" bg="white" placeholder="e.g. Panathur" value={form.name}
                onChange={e => setForm(p => p ? { ...p, name: e.target.value, lat: 0, lng: 0, city: '', state: '' } : p)}
              />
            </Box>

            <Box flex="1" minW="90px">
              <Text fontSize="xs" mb={1} fontWeight="medium">Radius (km) <Text as="span" color="red.400">*</Text></Text>
              <NumberInput size="sm" min={1}
                value={form.rangeInKmStr ?? String(form.rangeInKm)}
                onChange={val => setForm(p => p ? { ...p, rangeInKmStr: val, rangeInKm: val === '' ? p.rangeInKm : Number(val) } : p)}>
                <NumberInputField bg="white" />
              </NumberInput>
            </Box>

            <Box pt={5}>
              <Checkbox size="sm" colorScheme="teal" isChecked={form.isHospital}
                onChange={e => setForm(p => p ? { ...p, isHospital: e.target.checked } : p)}>
                Hospital Site
              </Checkbox>
            </Box>

            <Box pt={5}>
              <Checkbox size="sm" colorScheme="teal" isChecked={form.ambulancePlaced}
                onChange={e => setForm(p => p ? { ...p, ambulancePlaced: e.target.checked } : p)}>
                Ambulance Placed at Location
              </Checkbox>
            </Box>

            <Box pt={5}>
              <Button size="sm" variant="outline" colorScheme="teal"
                leftIcon={isLocating ? <Spinner size="xs" /> : <MdMap />}
                onClick={handleLocate} isDisabled={!form.name || isLocating}>
                {isLocating ? 'Locating...' : isLocated ? '📍 Re-locate' : '📍 Locate on Map'}
              </Button>
            </Box>

            <Box pt={5}>
              <Tooltip label={!isLocated ? 'Locate on map before saving' : ''} placement="top">
                <Button size="sm" colorScheme={isEditMode ? 'orange' : 'teal'}
                  leftIcon={isSaving ? <Spinner size="xs" /> : <MdCheck />}
                  onClick={handleSave} isDisabled={!form.name || !isLocated || isSaving}>
                  {isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
                </Button>
              </Tooltip>
            </Box>
          </Flex>

          {isLocated && (
            <Text fontSize="xs" color="teal.600">✓ {form.city || 'Located'} ({form.lat.toFixed(4)}, {form.lng.toFixed(4)})</Text>
          )}
        </Box>
      )}

      {/* Provider summary */}
      {selectedProvider && (
        <HStack mb={5} p={3} bg="teal.50" borderRadius="md" borderWidth={1} borderColor="teal.200" spacing={6}>
          <Text fontSize="sm"><strong>Operator:</strong> {selectedProvider.operatorName}</Text>
          <Text fontSize="sm"><strong>City:</strong> {selectedProvider.address?.city}</Text>
          <Text fontSize="sm"><strong>Total Areas:</strong> {coverageAreas.length}</Text>
          <Button size="xs" colorScheme="teal" leftIcon={<MdMap />} ml="auto" onClick={() => setCoverageMapOpen(true)} isDisabled={coverageAreas.length === 0}>
            Coverage Map
          </Button>
        </HStack>
      )}

      {/* Table */}
      {selectedProvider && (
        <TableContainer>
          <Flex mb={3} justify="space-between" align="center">
            <Text fontSize="sm" color="gray.500">
              {coverageAreas.filter(a => a.name?.toLowerCase().includes(coverageSearch.toLowerCase())).length} of {coverageAreas.length} area(s)
            </Text>
            <Input
              size="sm" maxW="240px" bg="gray.50"
              placeholder="Search coverage areas..."
              value={coverageSearch}
              onChange={e => { setCoverageSearch(e.target.value); setCoveragePage(1); }}
            />
          </Flex>
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>#</Th>
                <Th>Area Name</Th>
                <Th>City</Th>
                <Th>Range (km)</Th>
                <Th>Coordinates</Th>
                <Th>Is Hospital</Th>
                <Th>Ambulance Placed at Location</Th>
                <Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <Tr><Td colSpan={8} textAlign="center" py={8}><Spinner color="teal.500" /></Td></Tr>
              ) : coverageAreas.length === 0 ? (
                <Tr><Td colSpan={8} textAlign="center" py={8} color="gray.400">No coverage areas yet.</Td></Tr>
              ) : (() => {
                const filtered = coverageAreas.filter(a => a.name?.toLowerCase().includes(coverageSearch.toLowerCase()));
                const totalCoveragePages = Math.ceil(filtered.length / COVERAGE_PAGE_SIZE);
                const paginated = filtered.slice((coveragePage - 1) * COVERAGE_PAGE_SIZE, coveragePage * COVERAGE_PAGE_SIZE);
                return <>
                  {paginated.map((area, i) => (
                    <Tr key={area._id || i} _hover={{ bg: 'gray.50' }} bg={editingId === area._id ? 'orange.50' : undefined}>
                      <Td color="gray.400">{(coveragePage - 1) * COVERAGE_PAGE_SIZE + i + 1}</Td>
                      <Td fontWeight="medium">{area.name}</Td>
                      <Td>{area.city || '—'}</Td>
                      <Td>{area.rangeInKm} km</Td>
                      <Td fontSize="xs" color="gray.500">
                        {area.location?.coordinates
                          ? `${area.location.coordinates[1].toFixed(4)}, ${area.location.coordinates[0].toFixed(4)}`
                          : <Text as="span" color="red.300">No location</Text>}
                      </Td>
                      <Td><Badge colorScheme={area.isHospital ? 'green' : 'gray'}>{area.isHospital ? 'Yes' : 'No'}</Badge></Td>
                      <Td><Badge colorScheme={area.ambulancePlaced ? 'blue' : 'gray'}>{area.ambulancePlaced ? 'Yes' : 'No'}</Badge></Td>
                      <Td textAlign="right">
                        <HStack justify="flex-end" spacing={1}>
                          <IconButton icon={<MdEdit />} size="sm" variant="ghost" colorScheme="blue" aria-label="Edit" onClick={() => handleEdit(area)} />
                          <IconButton icon={<MdDelete />} size="sm" variant="ghost" colorScheme="red" aria-label="Delete" onClick={() => handleDelete(area)} />
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                  {totalCoveragePages > 1 && (
                    <Tr>
                      <Td colSpan={8} p={0}>
                        <Flex justify="center" align="center" py={3} gap={2}>
                          <Button size="sm" onClick={() => setCoveragePage(p => p - 1)} isDisabled={coveragePage === 1}>Prev</Button>
                          {Array.from({ length: totalCoveragePages }, (_, idx) => (
                            <Button
                              key={idx + 1}
                              size="sm"
                              variant={coveragePage === idx + 1 ? 'solid' : 'outline'}
                              colorScheme="teal"
                              onClick={() => setCoveragePage(idx + 1)}
                            >
                              {idx + 1}
                            </Button>
                          ))}
                          <Button size="sm" onClick={() => setCoveragePage(p => p + 1)} isDisabled={coveragePage === totalCoveragePages}>Next</Button>
                        </Flex>
                      </Td>
                    </Tr>
                  )}
                </>;
              })()}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {!selectedProvider && (
        <Flex justify="center" align="center" h="180px" color="gray.300" flexDir="column" gap={2}>
          <Box fontSize="3xl">🗺️</Box>
          <Text fontSize="md">Search and select a provider to manage its coverage areas</Text>
        </Flex>
      )}

      {form && (
        <MapPreviewModal
          isOpen={mapOpen}
          onClose={() => setMapOpen(false)}
          lat={form.lat} lng={form.lng}
          rangeInKm={form.rangeInKm}
          areaName={form.name}
          onChange={(lat, lng) => setForm(p => p ? { ...p, lat, lng } : p)}
        />
      )}

      <CoverageMapModal
        isOpen={coverageMapOpen}
        onClose={() => setCoverageMapOpen(false)}
        areas={coverageAreas}
        providerName={selectedProvider?.operatorName || ''}
      />
    </Box>
  );
}

export default ManageCoverageAreas;
